import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Support as SupportIcon,
  Send as SendIcon,
  CheckCircle as ResolvedIcon,
  SmartToy as AIIcon,
  School as GettingStartedIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import AIHelpAssistant from '../../Components/AIHelpAssistant';
import { Link } from 'react-router-dom';
import {
  createTicket,
  getUserTickets,
  getTicketById,
  addTicketResponse,
  markTicketAsRead,
  TICKET_STATUS,
  TICKET_PRIORITY,
  TICKET_CATEGORY,
  getStatusDisplayName,
  getPriorityDisplayName,
  getCategoryDisplayName
} from '../../services/helpDeskService';
import { format } from 'date-fns';
import logger from '../../utils/logger';

function HelpCenterPage() {
  const { currentUser } = useAuth();
  const { profile } = useProfile();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // New ticket form
  const [newTicket, setNewTicket] = useState({
    category: TICKET_CATEGORY.GENERAL_INQUIRY,
    priority: TICKET_PRIORITY.MEDIUM,
    subject: '',
    description: ''
  });

  // Reply form
  const [replyMessage, setReplyMessage] = useState('');

  const loadTickets = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const userTickets = await getUserTickets(currentUser.uid);
      setTickets(userTickets);
    } catch (err) {
      logger.error('Error loading tickets:', err);
      setError('Failed to load your tickets');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await createTicket({
        ...newTicket,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: profile?.displayName || `${profile?.firstName} ${profile?.lastName}` || currentUser.email,
        familyCode: profile?.familyCode || null
      });

      setSuccess('Your support ticket has been submitted. We will respond shortly!');
      setCreateDialogOpen(false);
      setNewTicket({
        category: TICKET_CATEGORY.GENERAL_INQUIRY,
        priority: TICKET_PRIORITY.MEDIUM,
        subject: '',
        description: ''
      });
      loadTickets();
    } catch (err) {
      logger.error('Error creating ticket:', err);
      setError('Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTicket = async (ticket) => {
    try {
      const fullTicket = await getTicketById(ticket.id);
      setSelectedTicket(fullTicket);
      setViewDialogOpen(true);

      // Mark as read if user hasn't read admin response
      if (!fullTicket.isRead) {
        await markTicketAsRead(ticket.id);
      }
    } catch (err) {
      logger.error('Error loading ticket:', err);
      setError('Failed to load ticket details');
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;

    try {
      setSubmitting(true);

      await addTicketResponse(selectedTicket.id, {
        message: replyMessage,
        authorId: currentUser.uid,
        authorName: profile?.displayName || currentUser.email,
        authorEmail: currentUser.email,
        isAdminResponse: false
      });

      setReplyMessage('');
      // Reload ticket to show new response
      const updated = await getTicketById(selectedTicket.id);
      setSelectedTicket(updated);
      setSuccess('Reply sent successfully');
    } catch (err) {
      logger.error('Error sending reply:', err);
      setError('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case TICKET_STATUS.OPEN: return 'info';
      case TICKET_STATUS.IN_PROGRESS: return 'warning';
      case TICKET_STATUS.WAITING_RESPONSE: return 'secondary';
      case TICKET_STATUS.RESOLVED: return 'success';
      case TICKET_STATUS.CLOSED: return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case TICKET_PRIORITY.URGENT: return 'error';
      case TICKET_PRIORITY.HIGH: return 'warning';
      case TICKET_PRIORITY.MEDIUM: return 'info';
      default: return 'default';
    }
  };

  const openTickets = tickets.filter(t => t.status !== TICKET_STATUS.CLOSED && t.status !== TICKET_STATUS.RESOLVED);
  const closedTickets = tickets.filter(t => t.status === TICKET_STATUS.CLOSED || t.status === TICKET_STATUS.RESOLVED);

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupportIcon color="primary" />
          Help Center
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New Support Ticket
        </Button>
      </Box>

      {/* Getting Started banner */}
      <Box
        component={Link}
        to="/tutorials"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          mb: 3,
          borderRadius: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          textDecoration: 'none',
          '&:hover': { bgcolor: 'primary.dark' },
          transition: 'background-color 0.2s'
        }}
      >
        <GettingStartedIcon sx={{ fontSize: 36, flexShrink: 0 }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            New to FamilyForLife? Start here
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Step-by-step Getting Started guide, feature tutorials, and FAQ — everything your family needs to get up and running.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* AI Help Assistant */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
              <Box sx={{
                p: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <AIIcon />
                <Typography variant="h6">Try AI First</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Get instant answers to common questions from our AI assistant:
                </Typography>
                <AIHelpAssistant mode="embedded" />
              </Box>
            </Paper>
          </Grid>

          {/* Open Tickets */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Active Tickets ({openTickets.length})
              </Typography>
              {openTickets.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No active tickets. Need help? Create a new support ticket!
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {openTickets.map(ticket => (
                    <Grid item xs={12} key={ticket.id}>
                      <Card variant="outlined" sx={{
                        borderLeft: 4,
                        borderLeftColor: `${getPriorityColor(ticket.priority)}.main`,
                        bgcolor: !ticket.isRead ? 'action.hover' : 'background.paper'
                      }}>
                        <CardContent sx={{ pb: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {ticket.subject}
                                {!ticket.isRead && (
                                  <Chip label="New Response" size="small" color="primary" sx={{ ml: 1 }} />
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {ticket.ticketNumber} · {getCategoryDisplayName(ticket.category)}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={1}>
                              <Chip
                                label={getStatusDisplayName(ticket.status)}
                                size="small"
                                color={getStatusColor(ticket.status)}
                              />
                              <Chip
                                label={getPriorityDisplayName(ticket.priority)}
                                size="small"
                                color={getPriorityColor(ticket.priority)}
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {ticket.description.substring(0, 100)}
                            {ticket.description.length > 100 && '...'}
                          </Typography>
                        </CardContent>
                        <CardActions>
                          <Button size="small" onClick={() => handleViewTicket(ticket)}>
                            View Details
                          </Button>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            Created: {ticket.createdAt?.toDate ? format(ticket.createdAt.toDate(), 'MMM d, yyyy') : 'Unknown'}
                          </Typography>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>

            {/* Closed Tickets */}
            {closedTickets.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  Resolved Tickets ({closedTickets.length})
                </Typography>
                <List dense>
                  {closedTickets.slice(0, 5).map(ticket => (
                    <ListItem
                      key={ticket.id}
                      button
                      onClick={() => handleViewTicket(ticket)}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemText
                        primary={ticket.subject}
                        secondary={`${ticket.ticketNumber} · ${getStatusDisplayName(ticket.status)}`}
                      />
                      <ResolvedIcon color="success" fontSize="small" />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {/* Create Ticket Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Support Ticket</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newTicket.category}
                  label="Category"
                  onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                >
                  {Object.values(TICKET_CATEGORY).map(cat => (
                    <MenuItem key={cat} value={cat}>{getCategoryDisplayName(cat)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newTicket.priority}
                  label="Priority"
                  onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                >
                  {Object.values(TICKET_PRIORITY).map(pri => (
                    <MenuItem key={pri} value={pri}>{getPriorityDisplayName(pri)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Subject"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                placeholder="Brief description of your issue"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={5}
                label="Description"
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                placeholder="Please provide as much detail as possible about your issue..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateTicket}
            variant="contained"
            disabled={submitting || !newTicket.subject || !newTicket.description}
            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {submitting ? 'Submitting...' : 'Submit Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Ticket Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">{selectedTicket?.subject}</Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedTicket?.ticketNumber}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Chip
                label={getStatusDisplayName(selectedTicket?.status)}
                size="small"
                color={getStatusColor(selectedTicket?.status)}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTicket && (
            <>
              {/* Original Message */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Original Request · {selectedTicket.createdAt?.toDate ? format(selectedTicket.createdAt.toDate(), 'MMM d, yyyy h:mm a') : ''}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTicket.description}
                  </Typography>
                </Paper>
                <Box display="flex" gap={1} mt={1}>
                  <Chip label={getCategoryDisplayName(selectedTicket.category)} size="small" />
                  <Chip label={getPriorityDisplayName(selectedTicket.priority)} size="small" color={getPriorityColor(selectedTicket.priority)} variant="outlined" />
                </Box>
              </Box>

              {/* Responses */}
              {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Conversation ({selectedTicket.responses.length} messages)
                  </Typography>
                  <List>
                    {selectedTicket.responses.map((response, index) => (
                      <ListItem
                        key={response.id || index}
                        sx={{
                          flexDirection: 'column',
                          alignItems: response.isAdminResponse ? 'flex-start' : 'flex-end',
                          px: 0
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '80%',
                            bgcolor: response.isAdminResponse ? 'primary.dark' : 'grey.800',
                            p: 2,
                            borderRadius: 2,
                            borderTopLeftRadius: response.isAdminResponse ? 0 : 2,
                            borderTopRightRadius: response.isAdminResponse ? 2 : 0
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            {response.isAdminResponse ? 'Support Team' : 'You'} ·{' '}
                            {response.createdAt?.toDate ? format(response.createdAt.toDate(), 'MMM d, h:mm a') : ''}
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {response.message}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Reply Box */}
              {selectedTicket.status !== TICKET_STATUS.CLOSED && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your reply..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                  />
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                      variant="contained"
                      onClick={handleSendReply}
                      disabled={!replyMessage.trim() || submitting}
                      startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                      Send Reply
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HelpCenterPage;
